'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { useState } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import type { ImageType } from '@eliadmin/core/src/api/api-types';

import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ABOUT_GALLERY_IMAGE_SIZES } from '../../constants/gallery-image-sizes';
import { Carousel } from '../ui/carousel';
import { Button } from '../ui/button';
import { IoSearchOutline } from 'react-icons/io5';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export interface Gallery {
  id: number;
  name: string;
  image: ImageType;
  images?: ImageType[];
  description: string;
}

export function GalleryCarousel({
  galleries,
  subtitle,
  title,
}: {
  galleries: Gallery[];
  subtitle: string;
  title: string;
}) {
  const options = {
    align: 'start' as const,
  };

  return (
    <CarouselProvider>
      <section
        id="nase-sluzby"
        className={cn(
          'flex w-full flex-col items-center justify-center gap-8 pb-8 pt-16 sm:gap-10 sm:pb-14 sm:pt-24 md:pb-16 md:pt-32 lg:gap-12 lg:pb-[72px] lg:pt-36',
        )}
      >
        <div className={cn('custom-container', 'flex items-end justify-between px-4 xl:px-0')}>
          <div className="flex flex-col items-start justify-start gap-4">
            <span className="text-sm text-muted-foreground sm:text-base">{subtitle}</span>
            <HtmlRendererRaw
              as="h2"
              className={cn(
                'relative z-[2] w-full max-w-[520px] flex-wrap items-start justify-start text-3xl font-bold tracking-tighter sm:text-4xl lg:text-[44px] lg:leading-[1.1]',
                'ea-primary-heading',
              )}
              html={title}
            />
          </div>
          <CarouselControls
            buttonProps={{ variant: 'primary-foreground', size: 'icon' }}
            className={{
              main: 'hidden gap-4 sm:flex',
              button: cn(
                'border-x-0 border-b-2 border-t-0 border-b-primary bg-primary-foreground text-black hover:bg-primary-foreground/90',
                'ea-services__carousel-control-button',
              ),
            }}
          />
        </div>
        <div className={cn('custom-container', 'relative pl-4 xl:pl-0')}>
          <div className="absolute -left-[1000px] top-0 z-[30] h-full w-[1000px] cursor-context-menu bg-white" />
          <Carousel
            options={options}
            className={{
              container: 'w-full gap-4 overflow-visible sm:gap-5 lg:gap-6',
              main: 'w-full overflow-visible',
              wrapper: 'w-full overflow-visible',
              slide: 'w-full max-w-[260px] sm:max-w-[320px] md:max-w-[450px] lg:max-w-[588px]',
            }}
            slides={galleries.map(gallery => (
              <GalleryCard key={gallery.id} gallery={gallery} />
            ))}
          />
        </div>
      </section>
    </CarouselProvider>
  );
}

export function GalleryCard({ gallery }: { gallery: Gallery }) {
  const { t } = useTranslations();
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  const allImages = [gallery.image, ...(gallery.images || [])];

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <div
        className={cn(
          'relative h-[195px] w-full max-w-[260px] overflow-hidden sm:h-[230px] sm:max-w-[420px] md:h-[330px] md:max-w-[520px] lg:h-[400px] lg:max-w-[588px] xl:h-[440px]',
          'group transition-all',
        )}
      >
        {gallery?.images && gallery.images.length > 0 && (
          <Button
            aria-label={`${t('text1944')}: ${gallery.name}`}
            onClick={handleOpenGallery(allImages, 0)}
            variant={'secondary'}
            size={'icon'}
            className="absolute bottom-4 left-4 z-10 cursor-pointer sm:bottom-6 sm:left-6 lg:bottom-8 lg:left-8"
          >
            <IoSearchOutline className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
        )}
        <div
          className={cn(
            'absolute inset-0 z-[1] h-full w-full bg-black/30 opacity-0',
            'transition-all group-hover:opacity-100',
          )}
        />
        <Image
          src={gallery.image.url}
          alt={resolveImageAlt(gallery?.image, gallery?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes={ABOUT_GALLERY_IMAGE_SIZES}
        />
      </div>
    </>
  );
}
